import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateExam.module.css';
import { stages } from '../../../data/studentsData';
import apiClient from '../../../api/client';
import { cairoLocalToISO } from '../../../utils/cairoDate';
import { MONTHLY_EXAM_ITEMS } from '../../../data/monthlyExamPlan';

const emptyQuestion = () => ({ text: '', options: ['', '', '', ''], correctAnswer: 0 });

// خيار "امتحان آخر (تحديد يدوي)" - عشان لسه نقدر نعمل أي امتحان حر زي الأول
const MANUAL_OPTION = '';

const CreateExam = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // القيمة المختارة من قائمة امتحانات الشهر الثابتة (فاضية = تحديد يدوي زي الأول)
  const [selectedPlanId, setSelectedPlanId] = useState(MANUAL_OPTION);
  const selectedPlanItem = MONTHLY_EXAM_ITEMS.find((it) => it.id === selectedPlanId) || null;

  // بيانات الامتحان الأساسية
  const [examInfo, setExamInfo] = useState({
    name: '',
    subject: '',
    stage: '',
    date: '',
    duration: 60,
    type: 'كويز سريع',
    maxScore: 100,
    availableFrom: '',
    availableUntil: '',
  });

  // حالة (State) لحفظ الأسئلة
  const [questions, setQuestions] = useState([emptyQuestion()]);

  // لما المدرس يختار امتحان من قائمة الشهر الثابتة، بنملأ بياناته أوتوماتيك
  // (الاسم، المادة، الدرجة الثابتة) وبنقفل درجة الامتحان عشان تفضل زي ما هي
  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    const item = MONTHLY_EXAM_ITEMS.find((it) => it.id === planId);
    if (item) {
      setExamInfo((prev) => ({
        ...prev,
        name: item.label,
        subject: item.subject,
        type: item.examType,
        maxScore: item.maxScore,
      }));
      // امتحان الورقي مالوش أسئلة على المنصة، درجته بتتسجل يدوي من صفحة "الدرجات"
      if (item.kind === 'paper') {
        setQuestions([]);
      } else if (questions.length === 0) {
        setQuestions([emptyQuestion()]);
      }
    }
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, emptyQuestion()]);
  };

  const handleRemoveQuestion = (qIndex) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== qIndex));
  };

  const handleQuestionTextChange = (qIndex, value) => {
    const updated = [...questions];
    updated[qIndex].text = value;
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const handleCorrectAnswerChange = (qIndex, optIndex) => {
    const updated = [...questions];
    updated[qIndex].correctAnswer = optIndex;
    setQuestions(updated);
  };

  const isPaperOnly = selectedPlanItem?.kind === 'paper';

  const handleSaveExam = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/exams', {
        ...examInfo,
        availableFrom: cairoLocalToISO(examInfo.availableFrom),
        availableUntil: cairoLocalToISO(examInfo.availableUntil),
        questions: isPaperOnly ? [] : questions,
      });
      alert(
        isPaperOnly
          ? 'تم إنشاء وعاء درجة الورقي! دلوقتي تقدر تدخل درجات الورقي من صفحة "الدرجات".'
          : 'تم حفظ الامتحان بنجاح! جاهز للطلاب عشان يمتحنوه.'
      );
      navigate('/exams');
    } catch (error) {
      const message = error.response?.data?.message || 'حصل خطأ أثناء حفظ الامتحان';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.pageWrapper} onSubmit={handleSaveExam}>
      <div className={styles.header}>
        <h1 className={styles.title}>إنشاء امتحان جديد</h1>
      </div>

      {/* القسم الجديد: اختيار الامتحان من قائمة الشهر الثابتة */}
      <div className={styles.formSection}>
        <h2 className={styles.sectionTitle}>اختيار الامتحان</h2>
        <div className={styles.formGroup}>
          <label>اختر الامتحان من قائمة امتحانات الشهر</label>
          <select
            className={styles.formSelect}
            value={selectedPlanId}
            onChange={(e) => handlePlanSelect(e.target.value)}
          >
            <option value={MANUAL_OPTION}>-- امتحان آخر (تحديد يدوي) --</option>
            {MONTHLY_EXAM_ITEMS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label} (درجته {item.maxScore})
              </option>
            ))}
          </select>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            {isPaperOnly
              ? 'ده امتحان ورقي بيتحل في السنتر - درجته هتتسجل يدوي من صفحة "الدرجات" بعد كده، ومحتاج بس تحدد المرحلة والتاريخ وتحفظ.'
              : 'لو اخترت امتحان من القائمة، الاسم والمادة والدرجة هيتملوا أوتوماتيك وميتغيروش.'}
          </p>
        </div>
      </div>

      {/* القسم الأول: تفاصيل الامتحان الأساسية */}
      <div className={styles.formSection}>
        <h2 className={styles.sectionTitle}>بيانات الامتحان</h2>
        <div className={styles.inputGrid}>
          <div className={styles.formGroup}>
            <label>اسم الامتحان (مثال: اختبار الفقه)</label>
            <input
              type="text"
              className={styles.formInput}
              required
              readOnly={!!selectedPlanItem}
              value={examInfo.name}
              onChange={(e) => setExamInfo({ ...examInfo, name: e.target.value })}
            />
          </div>

          <div className={styles.formGroup}>
            <label>المرحلة الدراسية</label>
            <select
              className={styles.formSelect}
              required
              value={examInfo.stage}
              onChange={(e) => setExamInfo({ ...examInfo, stage: e.target.value })}
            >
              <option value="">اختر المرحلة</option>
              {stages.map((stage, index) => (
                <option key={index} value={stage}>{stage}</option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>تاريخ الامتحان</label>
            <input
              type="date"
              className={styles.formInput}
              required
              value={examInfo.date}
              onChange={(e) => setExamInfo({ ...examInfo, date: e.target.value })}
            />
          </div>

          {!isPaperOnly && (
            <div className={styles.formGroup}>
              <label>مدة الامتحان (بالدقائق)</label>
              <input
                type="number"
                className={styles.formInput}
                placeholder="60"
                required
                value={examInfo.duration}
                onChange={(e) => setExamInfo({ ...examInfo, duration: Number(e.target.value) })}
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label>درجة الامتحان الكاملة</label>
            <input
              type="number"
              className={styles.formInput}
              placeholder="100"
              required
              readOnly={!!selectedPlanItem}
              value={examInfo.maxScore}
              onChange={(e) => setExamInfo({ ...examInfo, maxScore: Number(e.target.value) })}
            />
          </div>

          {!isPaperOnly && (
            <>
              <div className={styles.formGroup}>
                <label>الامتحان يفتح من (اختياري)</label>
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={examInfo.availableFrom}
                  onChange={(e) => setExamInfo({ ...examInfo, availableFrom: e.target.value })}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  لو حددته، الطالب هيشوف عداد تنازلي وميقدرش يبدأ قبل الميعاد ده
                </p>
              </div>

              <div className={styles.formGroup}>
                <label>الامتحان يقفل في (اختياري)</label>
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={examInfo.availableUntil}
                  onChange={(e) => setExamInfo({ ...examInfo, availableUntil: e.target.value })}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  لو حددته، الامتحان هيتقفل نهائي بعد الميعاد ده حتى لو الطالب مبدأش
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* القسم الثاني: إضافة الأسئلة - مش بيظهر خالص لو الامتحان "ورقي" */}
      {!isPaperOnly && (
        <div className={styles.formSection}>
          <h2 className={styles.sectionTitle}>أسئلة الامتحان</h2>

          {questions.map((q, qIndex) => (
            <div key={qIndex} className={styles.questionCard}>
              <div className={styles.formGroup}>
                <label>السؤال رقم {qIndex + 1}</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="اكتب صيغة السؤال هنا..."
                  required
                  value={q.text}
                  onChange={(e) => handleQuestionTextChange(qIndex, e.target.value)}
                />
              </div>

              <div className={styles.optionsGrid}>
                {q.options.map((opt, optIndex) => (
                  <label key={optIndex} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name={`correct-${qIndex}`}
                      checked={q.correctAnswer === optIndex}
                      onChange={() => handleCorrectAnswerChange(qIndex, optIndex)}
                      title="اختر ده لو هو الإجابة الصحيحة"
                    />
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder={`الاختيار ${optIndex + 1}`}
                      required
                      value={opt}
                      onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                    />
                  </label>
                ))}
              </div>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                ✅ حط علامة عند الاختيار الصحيح
              </p>

              {questions.length > 1 && (
                <button
                  type="button"
                  className={styles.addQuestionBtn}
                  style={{ backgroundColor: '#fee2e2', color: '#b91c1c', marginTop: '0.5rem' }}
                  onClick={() => handleRemoveQuestion(qIndex)}
                >
                  حذف السؤال ده
                </button>
              )}
            </div>
          ))}

          <button type="button" className={styles.addQuestionBtn} onClick={handleAddQuestion}>
            + إضافة سؤال آخر
          </button>
        </div>
      )}

      <button type="submit" className={styles.saveExamBtn} disabled={saving}>
        {saving ? 'جاري الحفظ...' : isPaperOnly ? 'حفظ وعاء درجة الورقي' : 'حفظ واعتماد الامتحان'}
      </button>
    </form>
  );
};

export default CreateExam;
